import React, { useCallback, useEffect, useState } from 'react';
import { adminAPI } from '../../../services/authService';
import { notifyError, notifySuccess } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';

const ConfigDropdownMasters = () => {
  const [masters, setMasters] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [selected, setSelected] = useState(null);
  const [options, setOptions] = useState([]);
  const [newMaster, setNewMaster] = useState({ masterKey: '', name: '', description: '' });
  const [newOpt, setNewOpt] = useState({ label: '', value: '', parentKey: '', optionKey: '' });
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    const res = await adminAPI.getConfigDropdownMasters({ includeOptions: 0 });
    setMasters(res?.data?.data || []);
    setEnabled(res?.data?.enabled !== false);
  }, []);

  useEffect(() => {
    reload().catch(() => notifyError('Unable to load dropdown masters'));
    adminAPI.getConfigFlags().then((r) => {
      const f = r?.data?.flags || {};
      if (f.ENABLE_CONFIG_RULES === false) setEnabled(false);
    }).catch(() => {});
  }, [reload]);

  const openMaster = async (m) => {
    setSelected(m);
    try {
      const res = await adminAPI.getConfigDropdownMaster(m.masterKey);
      setOptions(res?.data?.data?.options || []);
    } catch (_e) {
      notifyError('Unable to load options');
      setOptions([]);
    }
  };

  const createMaster = async () => {
    if (!newMaster.name.trim()) {
      notifyError('Name is required');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.createConfigDropdownMaster(newMaster);
      notifySuccess('Dropdown master created');
      setNewMaster({ masterKey: '', name: '', description: '' });
      await reload();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const addOption = async () => {
    if (!selected || !newOpt.label.trim()) {
      notifyError('Option label is required');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.upsertConfigDropdownOption(selected.id, newOpt);
      notifySuccess('Option saved');
      setNewOpt({ label: '', value: '', parentKey: '', optionKey: '' });
      await openMaster(selected);
      await reload();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Option save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeOption = async (opt) => {
    if (!(await confirmDialog(`Delete option "${opt.label}"?`, 'Delete option'))) return;
    try {
      await adminAPI.deleteConfigDropdownOption(opt.id);
      notifySuccess('Option deleted');
      await openMaster(selected);
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div>
      <p className="ce-muted">
        Phase 5: shared dropdown masters with optional parent/child options.
        Link a form field via dataSourceType=DROPDOWN_MASTER and dataSourceKey=masterKey
        {enabled ? '' : ' (ENABLE_CONFIG_RULES is off)'}.
      </p>

      <div className="ce-detail" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Create master</h3>
        <div className="form-row">
          <div className="form-group">
            <label>Name *</label>
            <input value={newMaster.name} onChange={(e) => setNewMaster({ ...newMaster, name: e.target.value })} disabled={!enabled} />
          </div>
          <div className="form-group">
            <label>Key (optional)</label>
            <input value={newMaster.masterKey} placeholder="auto from name" onChange={(e) => setNewMaster({ ...newMaster, masterKey: e.target.value })} disabled={!enabled} />
          </div>
        </div>
        <div className="form-group">
          <label>Description</label>
          <input value={newMaster.description} onChange={(e) => setNewMaster({ ...newMaster, description: e.target.value })} disabled={!enabled} />
        </div>
        <button type="button" className="btn-primary btn-sm" disabled={!enabled || saving} onClick={createMaster}>Create</button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr><th>Name</th><th>Key</th><th>Options</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {masters.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: '#64748b', padding: 24 }}>No masters yet</td></tr>
            ) : masters.map((m) => (
              <tr key={m.id}>
                <td>{m.name}</td>
                <td><code>{m.masterKey}</code></td>
                <td>{m.optionCount ?? '—'}</td>
                <td>{m.status}</td>
                <td><button type="button" className="btn-secondary btn-sm" onClick={() => openMaster(m)}>Manage options</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="ce-detail" style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Options — {selected.name} <span className="ce-badge">{selected.masterKey}</span></h3>
          <div className="form-row">
            <div className="form-group"><label>Label *</label><input value={newOpt.label} onChange={(e) => setNewOpt({ ...newOpt, label: e.target.value })} disabled={!enabled} /></div>
            <div className="form-group"><label>Value</label><input value={newOpt.value} onChange={(e) => setNewOpt({ ...newOpt, value: e.target.value })} disabled={!enabled} /></div>
            <div className="form-group"><label>Parent key</label><input value={newOpt.parentKey} placeholder="optional" onChange={(e) => setNewOpt({ ...newOpt, parentKey: e.target.value })} disabled={!enabled} /></div>
          </div>
          <button type="button" className="btn-primary btn-sm" disabled={!enabled || saving} onClick={addOption}>Add / update option</button>
          <div className="table-container" style={{ marginTop: 12 }}>
            <table className="data-table">
              <thead><tr><th>Label</th><th>Value</th><th>Key</th><th>Parent</th><th></th></tr></thead>
              <tbody>
                {options.map((o) => (
                  <tr key={o.id}>
                    <td>{o.label}</td>
                    <td>{o.value}</td>
                    <td><code>{o.optionKey}</code></td>
                    <td>{o.parentKey || '—'}</td>
                    <td><button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={() => removeOption(o)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigDropdownMasters;
